import {DatePicker} from '@github-ui/date-picker'
import type {
  IssueFieldSelection,
  IssueFieldSelectionValues,
  IssueFieldSingleSelectOption,
} from '@github-ui/issue-fields-shared/issue-field'
import {numberValidation} from '@github-ui/issue-metadata/IssueFieldNumberEditor'
import {IssueFieldTextEditorInternal} from '@github-ui/issue-metadata/IssueFieldTextEditor'
import {toIssueFieldWithUnsavedValue} from '@github-ui/issue-metadata/IssueFieldWithUnsavedValue'
import type {IssueFieldWithUnsavedValue} from '@github-ui/issue-metadata-types/issue-field-with-unsaved-value'
import type {IssueFieldMultiSelectOption} from '@github-ui/item-picker/IssueMultiSelectFieldPicker'
import {IssueMultiSelectFieldPicker} from '@github-ui/item-picker/IssueMultiSelectFieldPicker'
import {IssueSingleSelectFieldPicker} from '@github-ui/item-picker/IssueSingleSelectFieldPicker'
import type {ButtonProps} from '@primer/react'
import {useCallback, useMemo, useState} from 'react'

import {IssueFieldValueSharedEditorAnchor} from './IssueFieldValueSharedEditorAnchor'

const FALLBACK_WIDTH = '256px'
const NORMALIZED_HOUR = 12

export type IssueFieldValueSharedEditorProps = {
  field: IssueFieldWithUnsavedValue
  onChange: (field: IssueFieldWithUnsavedValue, value: IssueFieldSelectionValues) => void
  className?: string
  readonly?: boolean
  showVisibilityIndicator?: boolean
  isOwnerEnterpriseManaged?: boolean
}

export type IssueFieldValueSharedPendingEditorProps = {
  pendingField: IssueFieldSelection
  onCommit: (fieldId: string, fieldValue: IssueFieldSelectionValues) => void
  onClose: () => void
}

export const IssueFieldValueSharedPendingEditor = ({
  pendingField,
  onCommit,
  onClose,
}: IssueFieldValueSharedPendingEditorProps) => {
  return (
    <IssueFieldEditor
      field={toIssueFieldWithUnsavedValue(pendingField, null)}
      onCommit={onCommit}
      onClose={onClose}
      shouldOpen
    />
  )
}

export const IssueFieldValueSharedEditor = ({
  field,
  onChange,
  className,
  readonly,
  showVisibilityIndicator,
  isOwnerEnterpriseManaged,
}: IssueFieldValueSharedEditorProps) => {
  const onCommit = useCallback(
    (fieldId: string, value: IssueFieldSelectionValues) => {
      onChange(field, value)
    },
    [onChange, field],
  )
  if (!field.field.id || !field.field.name) return null

  return (
    <IssueFieldEditor
      field={field}
      onCommit={onCommit}
      className={className}
      readonly={readonly}
      showVisibilityIndicator={showVisibilityIndicator}
      isOwnerEnterpriseManaged={isOwnerEnterpriseManaged}
    />
  )
}

type EditorProps = {
  field: IssueFieldWithUnsavedValue
  onCommit: (fieldId: string, value: IssueFieldSelectionValues) => void
  anchorElement: (buttonProps: ButtonProps | undefined) => React.JSX.Element
  shouldOpen?: boolean
  onClose?: () => void
}

const IssueFieldNumberEditor = ({field, onCommit, anchorElement, shouldOpen = false, onClose}: EditorProps) => {
  return (
    <IssueFieldTextEditorInternal
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fieldId={field.field.id!}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fieldName={field.field.name!}
      initialValue={field.value as string}
      onCommit={onCommit}
      renderAnchor={anchorElement}
      overrideWidth={FALLBACK_WIDTH}
      isNumber
      validate={numberValidation}
      shouldOpen={shouldOpen}
      onClose={onClose}
    />
  )
}

const IssueFieldTextEditor = ({field, onCommit, anchorElement, shouldOpen = false, onClose}: EditorProps) => {
  return (
    <IssueFieldTextEditorInternal
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fieldId={field.field.id!}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fieldName={field.field.name!}
      initialValue={field.value as string}
      onCommit={onCommit}
      renderAnchor={anchorElement}
      overrideWidth={FALLBACK_WIDTH}
      shouldOpen={shouldOpen}
      onClose={onClose}
    />
  )
}

const IssueFieldDateEditor = ({field, onCommit, anchorElement, shouldOpen = false, onClose}: EditorProps) => {
  const [open, setOpen] = useState(shouldOpen)

  const datePickerValue = useMemo(() => {
    const value = field.value as Date | null
    if (!value) return null
    return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate())
  }, [field.value])

  const handleDateChange = useCallback(
    (date: Date | null) => {
      let normalizedDate: Date | null = null
      if (date) {
        normalizedDate = new Date(
          Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), NORMALIZED_HOUR, 0, 0, 0),
        )
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      onCommit(field.field.id!, normalizedDate)
    },
    [field.field.id, onCommit],
  )

  return (
    <DatePicker
      key={field.field.id}
      anchor={anchorProps => anchorElement(anchorProps)}
      showClearButton
      showTodayButton
      variant="single"
      dateFormat="long"
      onChange={handleDateChange}
      value={datePickerValue}
      placeholder="Set date"
      open={open}
      onOpen={() => {
        setOpen(true)
      }}
      onClose={() => {
        setOpen(false)
        onClose?.()
      }}
      anchoredOverlayProps={{
        side: 'outside-top',
      }}
    />
  )
}

const IssueFieldSingleSelectEditor = ({field, onCommit, anchorElement, shouldOpen = false, onClose}: EditorProps) => {
  const ssFieldValue = field.value as IssueFieldSingleSelectOption | null
  return (
    <IssueSingleSelectFieldPicker
      key={field.field.id}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fieldId={field.field.id!}
      fieldName={field.field.name || ''}
      selectedOption={ssFieldValue?.name || null}
      onSelectionChange={(selectedOption: IssueFieldSelectionValues) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        onCommit(field.field.id!, selectedOption)
      }}
      anchorElement={anchorProps => anchorElement(anchorProps)}
      readonly={false}
      isLazy
      shouldOpen={shouldOpen}
      onClose={onClose}
      insidePortal
    />
  )
}

const IssueFieldMultiSelectEditor = ({field, onCommit, anchorElement, shouldOpen = false, onClose}: EditorProps) => {
  const selectedOptions = (field.value as IssueFieldMultiSelectOption[] | null) ?? null
  return (
    <IssueMultiSelectFieldPicker
      key={field.field.id}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fieldId={field.field.id!}
      fieldName={field.field.name || ''}
      selectedOptions={selectedOptions}
      onSelectionChange={(options: IssueFieldMultiSelectOption[]) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        onCommit(field.field.id!, options.length > 0 ? options : null)
      }}
      anchorElement={anchorProps => anchorElement(anchorProps)}
      readonly={false}
      isLazy
      shouldOpen={shouldOpen}
      onClose={onClose}
      insidePortal
    />
  )
}

type IssueFieldEditorProps = {
  field: IssueFieldWithUnsavedValue
  onCommit: (fieldId: string, value: IssueFieldSelectionValues) => void
  onClose?: () => void
  shouldOpen?: boolean
  className?: string
  readonly?: boolean
  showVisibilityIndicator?: boolean
  isOwnerEnterpriseManaged?: boolean
}

const IssueFieldEditor = ({
  field,
  onCommit,
  onClose,
  shouldOpen = false,
  className,
  readonly,
  showVisibilityIndicator,
  isOwnerEnterpriseManaged,
}: IssueFieldEditorProps) => {
  const anchorElement = useCallback(
    (buttonProps: ButtonProps | undefined) => (
      <IssueFieldValueSharedEditorAnchor
        key={field.field.id}
        fieldWithValue={field}
        buttonProps={buttonProps}
        className={className}
        readonly={readonly}
        showVisibilityIndicator={showVisibilityIndicator}
        isOwnerEnterpriseManaged={isOwnerEnterpriseManaged}
      />
    ),
    [field, className, readonly, showVisibilityIndicator, isOwnerEnterpriseManaged],
  )

  switch (field.field.dataType) {
    case 'SINGLE_SELECT':
      return (
        <IssueFieldSingleSelectEditor
          field={field}
          onCommit={onCommit}
          anchorElement={anchorElement}
          shouldOpen={shouldOpen}
          onClose={onClose}
        />
      )
    case 'DATE':
      return (
        <IssueFieldDateEditor
          field={field}
          onCommit={onCommit}
          anchorElement={anchorElement}
          shouldOpen={shouldOpen}
          onClose={onClose}
        />
      )
    case 'TEXT':
      return (
        <IssueFieldTextEditor
          field={field}
          onCommit={onCommit}
          anchorElement={anchorElement}
          shouldOpen={shouldOpen}
          onClose={onClose}
        />
      )
    case 'NUMBER':
      return (
        <IssueFieldNumberEditor
          field={field}
          onCommit={onCommit}
          anchorElement={anchorElement}
          shouldOpen={shouldOpen}
          onClose={onClose}
        />
      )
    case 'MULTI_SELECT':
      return (
        <IssueFieldMultiSelectEditor
          field={field}
          onCommit={onCommit}
          anchorElement={anchorElement}
          shouldOpen={shouldOpen}
          onClose={onClose}
        />
      )
  }
}
